import type { InfiniteData, QueryKey } from '@tanstack/react-query';
import { hashKey, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { z } from 'zod/v4';
import { useAutoFetchAllPages } from '~/data-grid/hooks/useAutoFetchAllPages';
import { useFetch } from '~/hooks/useFetch';
import { warnIfExceedsInitialPage } from '~/utils/truncation';

/* -------------------------------------------------------------------------------------------------
 * Constants
 * -----------------------------------------------------------------------------------------------*/

export const chunkLoadingClassNames = [
  '[&_.ag-header-cell]:pointer-events-none',
  '[&_.ag-header-cell]:opacity-50',
  '[&_.ag-side-bar]:pointer-events-none',
  '[&_.ag-side-bar]:opacity-50',
].join(' ');

export const DEFAULT_CHUNK_SIZE = 50_000;
export const DEFAULT_FIRST_CHUNK_SIZE = 30_000;

/* -------------------------------------------------------------------------------------------------
 * Interfaces and types
 * -----------------------------------------------------------------------------------------------*/
// Rows live under `dataField`, which is `data` only by default, so the schema is only held to having a count
type BaseResponseSchema = z.ZodType<{ count: number; [field: string]: unknown }>;

type QueryParams = URLSearchParams | Record<string, string>;

type ChunkRow<TResponseSchema extends BaseResponseSchema, TDataField extends keyof z.infer<TResponseSchema>> =
  NonNullable<z.infer<TResponseSchema>[TDataField]> extends Array<infer TRow> ? TRow : never;

export interface UseChunkedGridDataQueryParams<
  TResponseSchema extends BaseResponseSchema,
  TDataField extends keyof z.infer<TResponseSchema> = 'data',
> {
  enabled?: boolean;
  queryKeyPrefix: QueryKey;
  route: string;
  responseSchema: TResponseSchema;
  params?: QueryParams;
  chunkSize?: number;
  firstChunkSize?: number;
  dataField?: TDataField;
  onChunkFetched?: (data: z.infer<TResponseSchema>) => void;
  autoFetchAll?: boolean;
  clearCacheOnUnmount?: boolean;
}

/* -------------------------------------------------------------------------------------------------
 * Helpers
 * -----------------------------------------------------------------------------------------------*/

// Normalise params to URLSearchParams
const normaliseParams = (params?: QueryParams) => {
  if (!params) {
    return undefined;
  }

  if (params instanceof URLSearchParams) {
    return params;
  }

  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, String(value));
    }
  });

  return searchParams;
};

const getChunkRows = <TResponseSchema extends BaseResponseSchema, TDataField extends keyof z.infer<TResponseSchema>>(
  page: z.infer<TResponseSchema>,
  dataField: TDataField,
) => page[dataField] as Array<ChunkRow<TResponseSchema, TDataField>>;

/* -------------------------------------------------------------------------------------------------
 * useChunkedGridDataQuery hook
 * -----------------------------------------------------------------------------------------------*/

export function useChunkedGridDataQuery<
  TResponseSchema extends BaseResponseSchema,
  TDataField extends keyof z.infer<TResponseSchema> = 'data',
>({
  enabled = true,
  dataField = 'data' as TDataField,
  queryKeyPrefix,
  route,
  responseSchema,
  params,
  chunkSize = DEFAULT_CHUNK_SIZE,
  firstChunkSize = DEFAULT_FIRST_CHUNK_SIZE,
  onChunkFetched,
  autoFetchAll = true,
  clearCacheOnUnmount = true,
}: UseChunkedGridDataQueryParams<TResponseSchema, TDataField>) {
  const { fetchData } = useFetch();
  const queryClient = useQueryClient();

  const normalisedParams = useMemo(() => normaliseParams(params), [params]);

  // Callers build a new prefix array each render, and a new key would re-run the unmount cleanup, so the prefix only
  // changes identity when its content does
  const [stableQueryKeyPrefix, setStableQueryKeyPrefix] = useState(queryKeyPrefix);
  if (hashKey(queryKeyPrefix) !== hashKey(stableQueryKeyPrefix)) {
    setStableQueryKeyPrefix(queryKeyPrefix);
  }
  const queryKey = useMemo(
    () => [...stableQueryKeyPrefix, normalisedParams?.toString()],
    [stableQueryKeyPrefix, normalisedParams],
  );

  const queryFn = useCallback(
    async ({ pageParam = 0 }: { pageParam: number }) => {
      const pageChunkSize = pageParam === 0 ? firstChunkSize : chunkSize;

      const pageParams = new URLSearchParams({
        limit: String(pageChunkSize),
        skip: String(pageParam),
      });

      if (normalisedParams) {
        normalisedParams.forEach((value, key) => {
          pageParams.append(key, value);
        });
      }

      const result = await fetchData(route, { response: responseSchema }, { queryParams: pageParams });

      if (pageParam === 0) {
        warnIfExceedsInitialPage({ count: result.count, endpoint: route, pageSize: firstChunkSize });
      }

      onChunkFetched?.(result);

      return result;
    },
    [chunkSize, firstChunkSize, normalisedParams, onChunkFetched, fetchData, responseSchema, route],
  );

  const getNextPageParam = useCallback(
    (lastPage: z.infer<TResponseSchema>, allPages: z.infer<TResponseSchema>[]) => {
      const totalLoaded = allPages.reduce((acc, page, index) => {
        const pageSize = index === 0 ? firstChunkSize : chunkSize;
        return acc + Math.min(getChunkRows(page, dataField).length, pageSize);
      }, 0);

      // An empty page would request the same offset again, so stop even if the count says more rows exist
      const hasMore = getChunkRows(lastPage, dataField).length > 0 && totalLoaded < lastPage.count;
      return hasMore ? totalLoaded : undefined;
    },
    [dataField, chunkSize, firstChunkSize],
  );

  const query = useInfiniteQuery({
    enabled,
    getNextPageParam,
    initialPageParam: 0,
    queryFn,
    queryKey,
    select: (response: InfiniteData<z.infer<TResponseSchema>, number>) => {
      const data = response.pages.flatMap((page) => getChunkRows(page, dataField));
      // Drop the first chunk's rows so they aren't returned next to the full `data` under `dataField`
      const { [dataField]: _firstChunkRows, ...firstPage } = response.pages[0];
      // Omitting `data` keeps the generic page type from intersecting it with the rows, which resolves to `any`
      // for schemas whose rows live under a different field.
      const rest = firstPage as Omit<z.infer<TResponseSchema>, 'count' | 'data' | TDataField>;
      const count: number = response.pages[0].count;

      return { ...rest, count, data };
    },
  });

  useAutoFetchAllPages({ enabled: autoFetchAll && enabled, loadedCount: query.data?.data.length ?? 0, query });

  const percentLoaded = useMemo(() => {
    const count = query.data?.count ?? 0;
    const data = query.data?.data ?? [];

    if (count === 0) {
      return 100;
    }

    return Math.round(((data.length ?? 0) / count) * 100);
  }, [query.data]);

  const metaData = useMemo(() => {
    if (!query.data) {
      return undefined;
    }

    // oxlint-disable-next-line no-unused-vars -- prefixing with _ breaks zod validation errors
    const { count, data, ...rest } = query.data;
    return rest;
  }, [query.data]);

  // Cancel and clear query cache on unmount
  useEffect(() => {
    return () => {
      if (clearCacheOnUnmount) {
        queryClient.cancelQueries({ queryKey });
        queryClient.removeQueries({ queryKey });
      }
    };
  }, [clearCacheOnUnmount, queryClient, queryKey]);

  // A failed chunk stops auto-fetch but leaves `hasNextPage` true, so callers waiting on every chunk would hang
  const isLoadingChunks = query.hasNextPage && !query.isFetchNextPageError;

  return {
    data: query.data?.data,
    error: query.error,
    isLoading: query.isLoading,
    // For callers that need the full dataset, such as searching or resolving a value that may sit in a later chunk
    isLoadingAll: query.isLoading || isLoadingChunks,
    isLoadingChunks,
    isFetching: query.isFetching,
    metaData,
    percentLoaded,
    query,
    rowCount: query.data?.count ?? 0,
    refetch: query.refetch,
  };
}
